import React, { useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom'
import { useParams } from 'react-router-dom'

// Assets and Components
import AppLogo from 'assets/logo.svg'
import NextLeadLoading from 'assets/next-lead-loading.svg'

import { Flex } from 'components'
import Box from 'components/Box'
import Button from 'components/Buttons/Button'
import NavigationTab from 'components/NavigationTab'
import { PopOverItem } from 'components/PopOver/types'
import Popup from 'components/Popup'
import ProfileDropdown from 'components/ProfileDropdown'
import Text from 'components/Text'
import Toast from 'components/Toast'

import { useAppSelector } from 'store'
import useModal from 'hooks/use-modal'
import { size } from 'utils/responsive'
import { FixMeAny } from 'types/global'
import { getNextLead } from 'services/lead'
import useWindowSize from 'hooks/use-window-size'
import useMeetingStatus from 'hooks/use-meeting-status'
import useUserPermissions from 'hooks/use-user-permissions'
import activeFeatures from 'utils/constants/activeFeatures'
import { FETCHING_STATUS, NEXT_LEAD_ERROR_CODES, Z_INDEX } from 'utils/constants'
import { useLoginMaster } from 'queries/master'
import { ROUTES } from '../../constants/constants'

import CustomProfileDropdownItems from './CustomProfileDropdownItems'
import ChangeBranchModal from './ChangeBranchModal'
import Notifications from './Notifications'
import GlobalSearch from './GlobalSearch'
import LiveCallStatus from './LiveCallStatus'
import MeetingStatus from './MeetingStatus'
import ChangeLanguageModal from './ChangeLanguageModal'
import { getLeadRoute } from 'utils/url'

const { fail } = FETCHING_STATUS
const { toastMessage } = Toast

type NavigationLink = {
  key: string
  path: string[]
  label: string
  externalLink: string[]
  isVisible?: boolean
}

type NavigationTab = NavigationLink & {
  active: boolean
}

export const profileOptions: PopOverItem[] = [
  { value: 'help', label: 'help', icon: ['far', 'question-circle'] },
  { value: 'logout', label: 'logout_btn', icon: ['far', 'sign-out'] },
]

const NavigationLink = ({ path, externalLink, ...link }: NavigationTab) => (
  <>
    {/* {confirmation} */}
    {externalLink.length > 0 ? (
      <Link to={{ pathname: `${externalLink[0]}` }} target="_blank">
        <NavigationTab variant="navbar" {...link} />
      </Link>
    ) : (
      <Link to={`/${path[0]}`}>
        <NavigationTab variant="navbar" {...link} />
      </Link>
    )}
  </>
)

interface IProps {
  children?: React.ReactNode
  [key: string]: FixMeAny
}

const showNoLeadInQueueFeedback = () => toastMessage({ message: 'task_queue_empty_success', variant: 'success' })

export default function withNavigationBar<P extends IProps>(WrappedComponent: React.ComponentType<P>) {
  const ComponentWithExtraInfo = (props: P) => {
    const { leadId = '' } = useParams<{ leadId: string }>()

    const { setConfirmationModal } = useModal()
    const [accountWorkflowPermissions] = useUserPermissions('accountWorkflow')
    const [dataWorkflowPermissions] = useUserPermissions('dataWorkflow')
    const [calendarPermissions] = useUserPermissions('calendar')
    const [reportsPermissions] = useUserPermissions('reports')

    const [isChangingBranch, setIsChangingBranch] = useState(false)
    const [isChangingLanguage, setIsChangingLanguage] = useState(false)
    const [isLoadingNextLead, setIsLoadingNextLead] = useState(false)

    const { pathname } = useLocation()

    const { data: meetingStatus } = useMeetingStatus()

    const currentPath = pathname.split('/').filter((i: string) => i)[0]
    const navigate = useNavigate()
    const { selectedBranch } = useAppSelector((state) => state.user)
    const {
      user: { user },
    } = useAppSelector((state) => state)
    const [master] = useLoginMaster()
    const windowSize = useWindowSize()

    const avatarPlaceHolder = `${user?.firstName?.substring(0, 1) ?? ''}${user?.lastName?.substring(0, 1) ?? ''}`

    const handleSubmitOverduePopup = async () => {
      const response = await getNextLead({ confirm_take_not_over_due_task: true })

      const message = 'fetching_data_error'

      if (response.status === fail) {
        toastMessage({ message: response.error ? response.error.message : message, variant: 'error' })
        return
      }

      if (!response.data.lead_id || response.data.error_code === NEXT_LEAD_ERROR_CODES.noNextLead) {
        showNoLeadInQueueFeedback()
        return
      }

      toastMessage({ actionKey: 'load', subject: 'next_lead_btn', variant: 'success' })
      navigate(getLeadRoute({ leadId: response.data.lead_id }))
    }

    const navigationLinks: NavigationLink[] = useMemo(() => {
      const links = [
        {
          key: 'dashboard',
          path: ['dashboard'],
          label: 'dashboard_lbl',
          externalLink: [],
          isVisible: true,
        },
        {
          key: 'leads',
          path: ['leads', 'lead'],
          label: 'leads_lbl',
          externalLink: [],
          isVisible: true,
        },
        {
          key: 'tasks',
          path: ['tasks'],
          label: 'tasks_lbl',
          externalLink: [],
          isVisible: true,
        },
        {
          key: 'report',
          path: ['reports', 'report'],
          externalLink: [],
          label: 'reports',
          isVisible: reportsPermissions.canView,
        },
        {
          key: 'my_calendar',
          path: ['calendar'],
          externalLink: [],
          label: 'calendar_lbl',
          isVisible: calendarPermissions.canView || calendarPermissions.canViewTeam,
        },
      ]

      activeFeatures.opportunity &&
        links.splice(2, 0, {
          key: 'opportunities',
          path: ['opportunities', 'opportunities'],
          label: 'opportunities_title_lbl',
          externalLink: [],
          isVisible: true,
        })

      return links
    }, [calendarPermissions, reportsPermissions])

    const logo = useMemo(() => {
      if (master?.logo_url) {
        return { showBorder: master.with_border_logo, url: master.logo_url }
      }

      return { showBorder: false, url: undefined }
    }, [master])

    const getNonOverdueTasksConfirmation = () => {
      setConfirmationModal({
        submit: handleSubmitOverduePopup,
        icon: ['fal', 'phone'],
        iconColor: 'gray5',
        okText: 'Proceed',
        name: 'overdueCall',
        title: 'task_queue_callback_overdue_empty_title',
        children: (
          <Box>
            <Text color="gray5" mb="15px">
              task_queue_callback_overdue_empty_desc
            </Text>
            <Text color="gray5" mb="15px">
              task_queue_callback_overdue_empty_proceed
            </Text>
          </Box>
        ),
      })
    }

    const handleClickNextLead = async () => {
      setIsLoadingNextLead(true)

      const response = await getNextLead({ confirm_take_not_over_due_task: false, lead_id: leadId })
      setIsLoadingNextLead(false)

      // Show error feedback
      let message = 'lead_next_queue_error'

      if (response.status === fail) {
        toastMessage({ message: response.error?.message || message, variant: 'error' })
        return
      }

      if (response.data.error_code === NEXT_LEAD_ERROR_CODES.noOverdueTaskLeft) {
        getNonOverdueTasksConfirmation()
        return
      }

      // User has no pending lead and no lead in queue
      if (!response.data.lead_id || response.data.error_code === NEXT_LEAD_ERROR_CODES.noNextLead) {
        showNoLeadInQueueFeedback()
        return
      }

      // User has no pending lead and next lead is overdue
      if (response.data.success) {
        toastMessage({ actionKey: 'load', subject: 'next_lead_btn', variant: 'success' })
        navigate(getLeadRoute({ leadId: response.data.lead_id }))
        return
      }

      // User has pending overdue lead
      if (!response.data.success) {
        if (response.data.is_overdue) {
          switch (response.data.error_code) {
            case NEXT_LEAD_ERROR_CODES.firstActivityNotClear:
              message = 'task_queue_activity_pending_error'
              break
            case NEXT_LEAD_ERROR_CODES.appointmentNotClear:
              message = 'task_queue_meeting_pending_error'
              break
            case NEXT_LEAD_ERROR_CODES.callTaskNotClear:
              message = 'task_queue_call_task_pending_error'
              break
            case NEXT_LEAD_ERROR_CODES.unselectLiveCallOutcome:
              message = 'unselected_live_call_outcome_error'
              break
            case NEXT_LEAD_ERROR_CODES.dispositionNotClear:
              message = 'next_lead_disposition_empty'
              break
            case NEXT_LEAD_ERROR_CODES.taskNotClear:
              message = 'task_queue_callback_pending_error'
              break
            default:
          }

          toastMessage({ message, variant: 'error' })
          navigate(getLeadRoute({ leadId: response.data.lead_id }))
          return
        } else {
          // User has call task not clear
          if (response.data.error_code === 'call_task_not_clear') {
            toastMessage({ message: 'task_queue_call_task_pending_error', variant: 'error' })
            navigate(getLeadRoute({ leadId: response.data.lead_id }))
            return
          } else if (response.data.error_code === 'unselect_live_call_outcome') {
            toastMessage({ message: 'unselected_live_call_outcome_error', variant: 'error' })
            navigate(getLeadRoute({ leadId: response.data.lead_id }))
            return
          }

          // User has lead in queue but not yet overdue
          getNonOverdueTasksConfirmation()
          return
        }
      }
    }

    return (
      <>
        {/* {confirmation} */}
        <PageContainer>
          <NavigationBar>
            <Link to={ROUTES.HOME}>
              <LogoWrapper>
                {logo.url ? <MasterLogo src={logo.url} showBorder={logo.showBorder} /> : <Logo />}
              </LogoWrapper>
            </Link>
            <NavigationContent>
              {navigationLinks.map(
                (link: NavigationLink) =>
                  link.isVisible && (
                    <NavigationLink {...link} key={link.key} active={link.path.includes(currentPath)} />
                  ),
              )}
            </NavigationContent>
            <Flex flex="1" justifyContent="end">
              <LiveCallStatus />
              {meetingStatus && meetingStatus.leadId && meetingStatus.leadAppointmentId && (
                <MeetingStatus leadId={meetingStatus.leadId} leadAppointmentId={meetingStatus.leadAppointmentId} />
              )}
              {accountWorkflowPermissions.canCreate && (
                <Button
                  data-umami-event="new_lead_clicked"
                  variant="outline"
                  icon={['fas', 'plus']}
                  onClick={() => navigate(`/${ROUTES.NEW_LEAD}`)}
                >
                  lead_add_btn
                </Button>
              )}
              {(accountWorkflowPermissions.canView || dataWorkflowPermissions.canView) && (
                <Button
                  data-umami-event="next_lead_clicked"
                  isSubmitting={isLoadingNextLead}
                  onClick={handleClickNextLead}
                >
                  next_lead_btn
                </Button>
              )}
              <GlobalSearch />
              <Notifications />
              <ProfileDropdown
                imgSrc={user?.photoUrl ?? undefined}
                firstName={user?.firstName || ''}
                lastName={user?.lastName || ''}
                options={profileOptions}
                showName={windowSize.width >= size.large}
                subText="welcome"
                avatarPlaceHolder={avatarPlaceHolder}
                customMenuList={
                  <CustomProfileDropdownItems
                    branchName={selectedBranch?.branchName}
                    onChangeBranch={() => setIsChangingBranch(true)}
                    onChangeLanguage={() => setIsChangingLanguage(true)}
                  />
                }
              />
            </Flex>
          </NavigationBar>
          <WrappedComponent {...props} />
          {isLoadingNextLead && (
            <Popup width="700px">
              <Box alignItems="center" display="flex" flexDirection="column" justifyContent="center" my={50}>
                <NextLeadLoading />
                <Text fontSize={16} fontWeight="bold" lineHeight="24px" mt={32}>
                  lead_queue_title
                </Text>
                <Text color="gray5" fontSize={14} lineHeight="24px">
                  task_queue_loading_desc_lbl
                </Text>
              </Box>
            </Popup>
          )}
        </PageContainer>
        <ChangeBranchModal visible={isChangingBranch} onClose={() => setIsChangingBranch(false)} />
        <ChangeLanguageModal visible={isChangingLanguage} onClose={() => setIsChangingLanguage(false)} />
      </>
    )
  }

  return ComponentWithExtraInfo
}

const PageContainer = styled.div`
  min-width: 960px;
  padding-top: 60px;
  box-sizing: border-box;
  height: 100%;
`

const NavigationBar = styled.div`
  display: flex;
  align-items: center;
  background-color: ${(p) => p.theme.colors.white};
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.6);
  color: white;
  height: 60px;
  margin: auto;
  padding: 0 15px;
  position: fixed;
  width: 100vw;
  top: 0;
  z-index: ${Z_INDEX.header};
`

const NavigationContent = styled.div`
  display: flex;
  align-items: center;
  overflow: overlay hidden;
  height: 100%;
  margin-right: 1rem;
`

const Link = styled(RouterLink)`
  display: flex;
  align-items: center;
  text-decoration: none;
`

const MasterLogo = styled.img<{ showBorder: boolean }>`
  height: 40px;
  width: 150px;
  ${(p) =>
    p.showBorder
      ? css`
          border: 1px solid ${p.theme.colors.gray4};
          border-radius: 4px;
          padding: 4px;
        `
      : ''}
`

const LogoWrapper = styled.div`
  padding: 0 16px;
  width: 200px;
`

const Logo = styled(AppLogo)`
  height: 100%;
  width: 100%;
`
